import { Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CamposDeLaFila, InstalacionDeInforme } from "@/lib/informes";
import { formatearMonto } from "@/lib/montos";
import { cn } from "@/lib/utils";

import { ChipDelCuadro } from "./ChipDelCuadro";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { FormularioDeNuevaInstalacion } from "./FormularioDeNuevaInstalacion";
import { TablaDeInstalaciones } from "./TablaDeInstalaciones";
import {
  formularioDesdeElItem,
  type ItemDeInstalacionPorEnlazar,
  type TerrenoDeLaLista,
} from "./instalacionesTerreno";
import {
  textoDelResumenDeInstalaciones,
  tituloDeLaListaDeInstalaciones,
  vistaDeInstalaciones,
  type FiltroDeInstalaciones,
} from "./cuadroDeInstalaciones";

interface ListaDeInstalacionesProps {
  filas: InstalacionDeInforme[];
  ocupada: boolean;
  onAgregar: (campos: CamposDeLaFila, fotos: File[]) => void;
  onAbrir: (fila: InstalacionDeInforme) => void;
  onValidar: (fila: InstalacionDeInforme, validada: boolean) => void;
  /** Lo que el encargado dejó en el predio; sin él la lista es la de siempre. */
  terreno?: TerrenoDeLaLista;
}

const FILTROS_DE_ESTADO: { filtro: FiltroDeInstalaciones; rotulo: string }[] = [
  { filtro: "sin_valorizar", rotulo: "Sin valorizar" },
  { filtro: "sin_validar", rotulo: "Sin validar" },
];

/**
 * La tarjeta 01: la lista de instalaciones y obras del informe.
 *
 * TODO LO QUE FILTRA Y CUENTA vive en `cuadroDeInstalaciones.ts`; acá solo
 * está el estado de la pantalla (qué filtro, qué búsqueda, si el formulario
 * está abierto) y su dibujo.
 *
 * EL ENCABEZADO LLEVA DOS TOTALES y no uno, como la tabla: reposición y
 * comercial son cifras independientes y ninguna resume a la otra.
 *
 * LAS INSTALACIONES CREADAS EN TERRENO SE ADOPTAN POR EL FORMULARIO DE ALTA:
 * tocar una abre "Nueva instalación u obra" precargado y el alta viaja con su
 * `terreno_ref`. Es el mismo POST de siempre —las instalaciones se guardan de
 * a una, no con la pestaña—, así que no hay estado de borrador que sobreviva.
 */
export function ListaDeInstalaciones({
  filas,
  ocupada,
  onAgregar,
  onAbrir,
  onValidar,
  terreno,
}: ListaDeInstalacionesProps) {
  const [formularioAbierto, setFormularioAbierto] = useState(false);
  /** La instalación de terreno que el formulario está completando, si la hay. */
  const [adoptando, setAdoptando] = useState<ItemDeInstalacionPorEnlazar | null>(null);
  const [abiertas, setAbiertas] = useState<ReadonlySet<string>>(new Set());

  function alternar(clave: string) {
    setAbiertas((actuales) => {
      const siguientes = new Set(actuales);
      if (!siguientes.delete(clave)) siguientes.add(clave);

      return siguientes;
    });
  }

  function cerrarElFormulario() {
    setFormularioAbierto(false);
    setAdoptando(null);
  }

  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState<FiltroDeInstalaciones>("todas");

  const vista = useMemo(
    () => vistaDeInstalaciones(filas, { busqueda, filtro }),
    [filas, busqueda, filtro],
  );

  const { resumen } = vista;
  // Sin ninguna instalación el formulario va abierto: es lo único que hay.
  const porEnlazar = terreno?.porEnlazar ?? [];
  const sinNada = filas.length === 0 && porEnlazar.length === 0;
  const mostrarFormulario = formularioAbierto || sinNada;
  const todasValidadas = resumen.cantidad > 0 && resumen.validadas === resumen.cantidad;
  const insignia = "bg-muted text-ink rounded-sm px-2 py-1 font-mono text-[0.6875rem] font-bold";

  return (
    <section
      id="instalaciones-lista"
      aria-labelledby="instalaciones-lista-titulo"
      className="bg-card scroll-mt-5 overflow-hidden rounded-xl border-[1.5px]"
    >
      <div className="px-5 pt-4 pb-3">
        <EncabezadoNumerado
          numero="01"
          idDelTitulo="instalaciones-lista-titulo"
          titulo={tituloDeLaListaDeInstalaciones(resumen.cantidad)}
          bajada={textoDelResumenDeInstalaciones(resumen)}
          acciones={
            <>
              {resumen.cantidad > 0 ? (
                <>
                  <span
                    className={cn(
                      "rounded-sm px-2 py-1 font-mono text-[0.6875rem] font-bold",
                      todasValidadas
                        ? "bg-success-soft text-success-soft-foreground"
                        : "bg-muted text-ink-body",
                    )}
                  >
                    {resumen.validadas} de {resumen.cantidad} validadas
                  </span>
                  <span className={insignia}>Rep. {formatearMonto(resumen.totalReposicion, null)} UF</span>
                  <span className={insignia}>Com. {formatearMonto(resumen.totalComercial, null)} UF</span>
                </>
              ) : null}
              {mostrarFormulario ? null : (
                <Button
                  type="button"
                  size="sm"
                  // El verde más fuerte del diseño (#2b8229), no el primario:
                  // sobre la franja verde de la IA el primario no se despega.
                  className="bg-primary-fuerte hover:bg-primary-fuerte hover:brightness-90"
                  onClick={() => {
                    setAdoptando(null);
                    setFormularioAbierto(true);
                  }}
                >
                  + Agregar instalación
                </Button>
              )}
            </>
          }
        />
      </div>

      {mostrarFormulario ? (
        <FormularioDeNuevaInstalacion
          // Una instalación de terreno distinta es OTRO borrador: se remonta.
          key={adoptando?.uuid ?? "a-mano"}
          ocupada={ocupada}
          inicial={adoptando ? formularioDesdeElItem(adoptando.item) : undefined}
          onAgregar={(campos, fotos) =>
            onAgregar(adoptando ? { ...campos, terreno_ref: adoptando.uuid } : campos, fotos)
          }
          onCerrar={cerrarElFormulario}
        />
      ) : null}

      {sinNada ? null : (
        <>
          <div className="border-divisor bg-muted/30 flex flex-wrap items-center gap-2.5 border-t px-5 py-2.5">
            <div className="relative w-[14.375rem] flex-none">
              <Search
                className="text-ink-hint pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2"
                aria-hidden="true"
              />
              <Input
                aria-label="Buscar instalación u obra"
                placeholder="Buscar instalación u obra"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="bg-background h-8 rounded-[0.625rem] pl-8 text-[0.78125rem]"
              />
            </div>

            <div className="flex flex-wrap gap-1.5">
              {FILTROS_DE_ESTADO.map((f) => (
                <ChipDelCuadro
                  key={f.filtro}
                  activo={filtro === f.filtro}
                  rotulo={f.rotulo}
                  cantidad={vista.conteos[f.filtro]}
                  onClick={() => setFiltro(filtro === f.filtro ? "todas" : f.filtro)}
                />
              ))}
            </div>
          </div>

          {vista.visibles.length > 0 || porEnlazar.length > 0 ? (
            <div className="border-divisor-suave overflow-x-auto border-t">
              <TablaDeInstalaciones
                filas={vista.visibles}
                todas={filas}
                ocupada={ocupada}
                onAbrir={onAbrir}
                onValidar={onValidar}
                terreno={terreno}
                abiertas={abiertas}
                onAlternar={alternar}
                onAdoptar={(porEnlazar) => {
                  setAdoptando(porEnlazar);
                  setFormularioAbierto(true);
                }}
              />
            </div>
          ) : (
            <p className="border-divisor-suave text-ink-muted border-t px-5 py-5.5 text-center text-[0.78125rem] font-semibold">
              Ninguna instalación coincide con la búsqueda o el filtro.
            </p>
          )}

          <div className="border-divisor-suave bg-superficie-sutil border-t px-5 py-2.5 text-right">
            <span className="text-ink-muted text-[0.6875rem] font-semibold">
              Clic en una fila para ver el detalle y editar.
            </span>
          </div>
        </>
      )}
    </section>
  );
}
